const WEBMCP_TOOLS_PATH = '/webmcp/tools';

const STATUS = {
  available: {
    text: 'WebMCP available',
    title: 'This browser can use in-tab fleet inventory tools (ChatGPT desktop or Chrome with WebMCP).',
  },
  unavailable: {
    text: 'WebMCP unavailable',
    title: 'This browser does not expose WebMCP. Use ChatGPT desktop or Chrome with WebMCP enabled.',
  },
};

let lastSupportResult;

export function applyWebmcpSupportStatus(result, { document: doc = document } = {}) {
  lastSupportResult = result;
  const labels = doc.querySelectorAll('[data-webmcp-support-status]');
  if (!labels.length) return;

  if (result.reason === 'not-signed-in') {
    labels.forEach((el) => { el.hidden = true; });
    return;
  }

  const state = result.registered ? STATUS.available : STATUS.unavailable;
  labels.forEach((el) => {
    el.hidden = false;
    el.textContent = state.text;
    el.classList.remove('green', 'grey');
    el.setAttribute('title', state.title);
  });
}

function restoreWebmcpSupportStatus() {
  if (lastSupportResult) applyWebmcpSupportStatus(lastSupportResult);
}

export async function registerWebmcpTools({ document: doc = document, fetchImpl = fetch } = {}) {
  if (doc.body?.dataset?.webmcp !== 'true') {
    const result = { registered: false, reason: 'not-signed-in' };
    applyWebmcpSupportStatus(result, { document: doc });
    return result;
  }

  const modelContext = doc.modelContext;
  if (!modelContext || typeof modelContext.registerTool !== 'function') {
    const result = { registered: false, reason: 'unsupported' };
    applyWebmcpSupportStatus(result, { document: doc });
    return result;
  }

  const catalogEl = doc.getElementById('webmcp-tool-catalog');
  if (!catalogEl) {
    const result = { registered: false, reason: 'no-catalog' };
    applyWebmcpSupportStatus(result, { document: doc });
    return result;
  }

  const tools = JSON.parse(catalogEl.textContent);
  try {
    await Promise.all(tools.map((tool) =>
      modelContext.registerTool({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema,
        annotations: tool.annotations,
        execute: async (args, extras = {}) => {
          const csrf = doc.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
          const response = await fetchImpl(WEBMCP_TOOLS_PATH, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrf,
            },
            body: JSON.stringify({ name: tool.name, arguments: args || {} }),
            signal: extras.signal,
          });
          const json = await response.json();
          return JSON.stringify(json.structuredContent ?? json);
        },
      })
    ));
  } catch {
    const result = { registered: false, reason: 'unsupported' };
    applyWebmcpSupportStatus(result, { document: doc });
    return result;
  }

  const result = { registered: true };
  applyWebmcpSupportStatus(result, { document: doc });
  return result;
}

void registerWebmcpTools();
document.addEventListener('turbo:render', restoreWebmcpSupportStatus);
document.addEventListener('turbo:load', restoreWebmcpSupportStatus);
